<template>
  <licence-editor
    v-if="copyEditLicence"
    ref="customLicenceForm"
    :open="showEditLicenceDialog"
    :editing="true"
    :licence="copyEditLicence"
    :features="copyEditLicence.features || []"
    :definitions="features"
    :parents="parentLicences"
    :selected-scopes.sync="selectedScopes"
    :loading="loading"
    :loading-parents="loadingParents"
    :loading-features="loadingFeatures"
    :feature-error="featureError"
    @retry-features="initialize"
    @close="closeEditLicenceDialog"
    @save="editLicence"
  />
</template>

<script>
import Swal from 'sweetalert2'
import { apiService } from '../../service/api.service'
import LicenceEditor from './LicenceEditor.vue'

export default {
  components: { LicenceEditor },
  props: {
    licenceInfoForEdit: { type: Object, default: () => ({}) },
    showEditLicenceDialog: { type: Boolean, default: false }
  },

  watch: {
    showEditLicenceDialog (val) {
      if (val) {
        this.initialize()
      }
    }
  },

  created () {
    this.initialize()
  },

  data: () => ({
    features: [],
    copyEditLicence: null,
    selectedScopes: [],
    loading: false,
    loadingFeatures: true,
    featureError: false,
    loadingParents: false,
    parentLicences: []

  }),

  methods: {
    async initialize () {
      this.loadingFeatures = true
      this.featureError = false
      this.copyEditLicence = JSON.parse(
        JSON.stringify(this.licenceInfoForEdit)
      )
      this.$set(this.copyEditLicence, 'parent_id', this.copyEditLicence.group_id || null)
      this.selectedScopes = this.copyEditLicence.scopes || []
      this.loadParentLicences()
      await apiService
        .get('admin-get-features')
        .then((res) => {
          this.features = res.data.data

          // Merge existing licence features with all available features
          const allFeatures = []
          for (let index = 0; index < this.features.length; index++) {
            const feature = this.features[index]

            // Find if this feature exists in current licence
            const existingFeature = this.copyEditLicence.features?.find(
              (f) => f.feature_id === feature.feature_id
            )

            if (existingFeature) {
              // Use existing feature data
              allFeatures.push(existingFeature)
            } else {
              // Add missing feature with default value
              const defaultLimit = feature.label === 'GPU Limits' ? -1 : 0
              allFeatures.push({
                feature_id: feature.feature_id,
                feature_name: feature.label,
                limit: defaultLimit
              })
            }
          }

          // Update copyEditLicence with complete features list
          this.copyEditLicence.features = allFeatures
        })
        .catch(() => {
          this.featureError = true
        })
        .finally(() => { this.loadingFeatures = false })
    },

    async editLicence () {
      if (!this.loading && this.$refs.customLicenceForm.validate()) {
        this.loading = true
        this.copyEditLicence.scopes = this.selectedScopes
        this.copyEditLicence.parent_id = this.copyEditLicence.parent_id || null
        this.copyEditLicence.group_id = this.copyEditLicence.parent_id
        await apiService
          .post('admin-edit-licence', this.copyEditLicence)
          .then(() => {
            this.closeEditLicenceDialog()
            Swal.fire(
              'Edited!',
              'Licence is edited to user successfully!',
              'success'
            )
          })
          .catch((err) => {
            const errMsg = err.response?.data.message
              ? err.response.data.message
              : 'An error occurred while editing the license!'

            Swal.fire('Error!', errMsg, 'error')
          })
          .finally(() => { this.loading = false })
      }
    },

    async loadParentLicences () {
      this.loadingParents = true
      try {
        const { data } = await apiService.get('admin-parent-licences')
        this.parentLicences = (data.data || []).filter((p) => p.is_active || p.id === this.copyEditLicence.parent_id)
      } catch (err) {
        console.error('Error loading parent licences:', err)
      } finally {
        this.loadingParents = false
      }
    },

    closeEditLicenceDialog () {
      this.$emit('closeEditLicenceDialog')
    }
  }
}
</script>
